// CurrencyInput — controlled text input that displays the numeric value
// with en-AU thousands separators while emitting a digits-only raw string
// to the parent. Used by SOW + variation forms so users see "120,000"
// while the form state and wire payload retain the integer-dollar shape
// the backend expects.
//
// The raw value is the source of truth: the parent owns it as a string
// of digits (with optional leading minus when allowNegative is set);
// the input element renders a derived formatted display. A lone "-" is
// treated as a transient state so the user can type a negative value
// before any digits land.
//
// Caret position is preserved across reformats by counting digits to
// the left of the caret before the change, then re-locating that same
// digit-count position in the formatted display after the change. This
// avoids the caret jumping to the end of the field whenever a comma
// is inserted or removed.

import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  type ChangeEvent,
} from "react";

export interface CurrencyInputProps {
  /** Raw string. Digits only by default; with `allowDecimal` a single
   *  decimal point and up to 2 fractional digits are also permitted.
   *  An optional leading minus is preserved when `allowNegative` is set.
   *  The empty string represents an unset value. */
  value: string;
  onChange: (rawValue: string) => void;
  ariaLabel: string;
  ariaInvalid?: boolean;
  ariaDescribedBy?: string;
  allowNegative?: boolean;
  /** Allow up to 2 decimal places. When true the regex permits `.` once
   *  and trims fractional input to the first 2 digits. */
  allowDecimal?: boolean;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  id?: string;
  testId?: string;
}

const FORMATTER = new Intl.NumberFormat("en-AU");

/** Strip non-digits (and, when allowDecimal is set, all but one `.`)
 *  from a typed display, preserving an optional leading minus when
 *  allowNegative is set. A lone "-" is preserved so the user can type a
 *  negative value before any digits land. A trailing "." (e.g. "120.")
 *  is preserved as a transient state so the user can type the integer
 *  part, then the dot, then the fractional digits without round-trip
 *  loss in form state. */
function rawFromDisplay(
  input: string,
  allowNegative: boolean,
  allowDecimal: boolean,
): string {
  const trimmed = input.replace(/\s/g, "");
  const negative = allowNegative && trimmed.startsWith("-");
  const body = negative ? trimmed.slice(1) : trimmed;

  let result: string;
  if (allowDecimal) {
    const cleaned = body.replace(/[^0-9.]/g, "");
    const firstDot = cleaned.indexOf(".");
    if (firstDot === -1) {
      result = cleaned;
    } else {
      const intPart = cleaned.slice(0, firstDot);
      const decPart = cleaned
        .slice(firstDot + 1)
        .replace(/\./g, "")
        .slice(0, 2);
      result = `${intPart}.${decPart}`;
    }
  } else {
    result = body.replace(/[^0-9]/g, "");
  }

  if (negative && result === "") return "-";
  return negative ? `-${result}` : result;
}

/** Render a raw value for the input element. Empty stays empty; a lone
 *  "-" stays "-"; otherwise the integer part is grouped via Intl while
 *  the fractional part (if any) is left as-typed. */
function displayFromRaw(raw: string, allowDecimal: boolean): string {
  if (raw === "" || raw === "-") return raw;
  const negative = raw.startsWith("-");
  const body = negative ? raw.slice(1) : raw;
  if (body === "") return raw;

  if (allowDecimal && body.includes(".")) {
    const dotIdx = body.indexOf(".");
    const intPart = body.slice(0, dotIdx);
    const decPart = body.slice(dotIdx + 1);
    if (intPart === "") {
      // Trailing-dot-only ".5" → render as "0.5" only after the user
      // commits a digit; here keep as typed so a lone "." stays visible.
      const formatted = `.${decPart}`;
      return negative ? `-${formatted}` : formatted;
    }
    const intNum = Number(intPart);
    if (!Number.isFinite(intNum)) return raw;
    const formattedInt = FORMATTER.format(intNum);
    const formatted = `${formattedInt}.${decPart}`;
    return negative ? `-${formatted}` : formatted;
  }

  const n = Number(body);
  if (!Number.isFinite(n)) return raw;
  const formatted = FORMATTER.format(n);
  return negative ? `-${formatted}` : formatted;
}

/** Count digits (and the decimal point, when allowDecimal is set)
 *  at-or-before the caret index in a formatted string. The result is
 *  the index in the underlying raw-digit stream. */
function digitsBeforeCaret(
  display: string,
  caret: number,
  allowDecimal: boolean,
): number {
  let count = 0;
  for (let i = 0; i < caret && i < display.length; i++) {
    const c = display[i];
    if (/[0-9]/.test(c)) count++;
    else if (allowDecimal && c === ".") count++;
  }
  return count;
}

/** Walk the formatted display until we've seen `digitTarget` significant
 *  characters (digits, plus the decimal point in allowDecimal mode);
 *  return the resulting index. Used to map a digit-count position back
 *  into the formatted-display caret position after a reformat. */
function caretFromDigitsBefore(
  display: string,
  digitTarget: number,
  allowDecimal: boolean,
): number {
  let seen = 0;
  for (let i = 0; i < display.length; i++) {
    if (seen === digitTarget) return i;
    const c = display[i];
    if (/[0-9]/.test(c)) seen++;
    else if (allowDecimal && c === ".") seen++;
  }
  return display.length;
}

const CurrencyInput = forwardRef<HTMLInputElement, CurrencyInputProps>(
  function CurrencyInput(
    {
      value,
      onChange,
      ariaLabel,
      ariaInvalid,
      ariaDescribedBy,
      allowNegative = false,
      allowDecimal = false,
      disabled,
      placeholder,
      className = "input pl-7 tabular-nums",
      id,
      testId,
    },
    ref,
  ) {
    const inputRef = useRef<HTMLInputElement | null>(null);
    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement, []);

    const display = displayFromRaw(value, allowDecimal);
    const pendingCaretRef = useRef<number | null>(null);

    // Restore the caret after React re-renders with the new formatted
    // value. Without this hook the caret jumps to the end of the field
    // whenever a comma is inserted or removed mid-string.
    useEffect(() => {
      const el = inputRef.current;
      if (el && pendingCaretRef.current !== null) {
        const pos = pendingCaretRef.current;
        try {
          el.setSelectionRange(pos, pos);
        } catch {
          // Safari throws on number inputs; we use type=text so this is
          // defensive only.
        }
        pendingCaretRef.current = null;
      }
    }, [display]);

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      const typed = e.target.value;
      const caretBefore = e.target.selectionStart ?? typed.length;
      const digitsBefore = digitsBeforeCaret(typed, caretBefore, allowDecimal);
      const raw = rawFromDisplay(typed, allowNegative, allowDecimal);
      const nextDisplay = displayFromRaw(raw, allowDecimal);
      // The minus sign is structural (it determines the negative branch),
      // not a digit — caretFromDigitsBefore counts digits only. When the
      // display starts with a minus we offset the caret by one so a
      // subsequent keystroke lands AFTER the minus, not in front of it.
      // Without this offset typing "-5" would route the "5" to position
      // 0, producing "5-" → raw "5", which silently flips the sign.
      const minusOffset = nextDisplay.startsWith("-") ? 1 : 0;
      const nextCaret =
        caretFromDigitsBefore(nextDisplay, digitsBefore, allowDecimal) +
        minusOffset;
      pendingCaretRef.current = Math.min(nextCaret, nextDisplay.length);
      onChange(raw);
    };

    return (
      <input
        ref={inputRef}
        id={id}
        type="text"
        inputMode={
          allowNegative ? "text" : allowDecimal ? "decimal" : "numeric"
        }
        className={className}
        value={display}
        onChange={handleChange}
        disabled={disabled}
        placeholder={placeholder}
        aria-label={ariaLabel}
        aria-invalid={ariaInvalid || undefined}
        aria-describedby={ariaDescribedBy}
        data-testid={testId}
      />
    );
  },
);

export default CurrencyInput;
